'use client'

import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { User } from '@supabase/supabase-js'
import { createClient } from '@/lib/supabase/client'
import { useRouter } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence } from 'framer-motion'
import InterstitialLoading from '@/app/components/InterstitialLoading'
import { decodeAppRoleFromJwt, DEFAULT_APP_ROLE, type AppRole } from '@/lib/auth/role'

// Define the lightweight profile shape returned by backend proxy
type UserProfile = {
  rmr: number;
  maintenanceCalories: number;
  weight: number;
  age: number;
};

type AuthContextType = {
  user: User | null
  loading: boolean
  isSigningOut: boolean
  profile: UserProfile | null;
  /** Application role from the JWT. Defaults to 'client'. UX only -- RLS is the gate. */
  appRole: AppRole;
  signIn: (email: string, password: string) => Promise<{ hasProfile: boolean; appRole: AppRole }>;
  signOut: () => Promise<void>
  supabase: ReturnType<typeof createClient>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [appRole, setAppRole] = useState<AppRole>(DEFAULT_APP_ROLE);
  const [loading, setLoading] = useState(true)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const router = useRouter()
  const queryClient = useQueryClient()

  // Use the centralized client creation utility
  const supabase = createClient()

  const fetchProfileWithRetry = useCallback(async (_userId: string, maxRetries = 2): Promise<UserProfile | null> => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)

        const response = await fetch('/api/user/profile', {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal
        })

        clearTimeout(timeoutId)

        if (response.status === 404) {
          return null
        }

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}))
          console.error(`Profile fetch error (attempt ${attempt + 1}):`, errorBody)
          if (attempt === maxRetries) {
            return null
          }
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
          continue
        }

        const profileData = await response.json()
        return profileData as UserProfile
      } catch (error) {
        console.error(`Profile fetch failed (attempt ${attempt + 1}):`, error)
        if (attempt === maxRetries) {
          return null
        }
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
      }
    }
    return null
  }, [])

  const signOut = useCallback(async () => {
    setIsSigningOut(true)
    try {
      const signOutPromise = supabase.auth.signOut()
      const minDisplayPromise = new Promise(resolve => setTimeout(resolve, 1000));
      await Promise.all([signOutPromise, minDisplayPromise]);
    } catch (error) {
      console.error('Error during sign out:', error)
    } finally {
      // State cleanup will be handled by onAuthStateChange SIGNED_OUT event
      // Don't manually clear state here to avoid race conditions
      setIsSigningOut(false)
    }
  }, [supabase]);

  const retryAuth = useCallback(() => {
    setAuthError(null)
    setLoading(true)
    // Force a page reload to retry auth initialization
    window.location.reload()
  }, [])

  // Effect for handling auth state changes from Supabase
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`[AUTH] Supabase auth event: ${event}`);
      setAuthError(null);
      setUser(session?.user ?? null);
      // Read the role off the session token. TOKEN_REFRESHED fires here too, so
      // a role change picked up on refresh propagates without a reload.
      setAppRole(decodeAppRoleFromJwt(session?.access_token));
      setLoading(false); // Initial loading is finished once we get any auth event

      if (event === 'SIGNED_OUT') {
        queryClient.clear();
        setAppRole(DEFAULT_APP_ROLE);
        router.push('/');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient, router, supabase.auth]);

  // Effect for fetching user profile when user object changes
  useEffect(() => {
    if (user) {
      fetchProfileWithRetry(user.id).then(setProfile);
    } else {
      setProfile(null);
    }
  }, [user, fetchProfileWithRetry]);

  // Timeout effect to prevent getting stuck on initializing
  useEffect(() => {
    if (!loading) return; // Only run when loading
    const timer = setTimeout(() => {
      console.error("❌ [AUTH] Session check timed out.");
      setAuthError("Session check timed out. Please refresh the page.");
      setLoading(false);
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, [loading]);


  const signIn = async (email: string, password: string) => {
    setAuthError(null) // Clear any previous errors
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      throw error
    }

    if (data.user) {
      const signedInRole = decodeAppRoleFromJwt(data.session?.access_token)

      // Trainers and admins have no user_profiles row by design (it holds
      // biometrics), so skip the lookup rather than reporting hasProfile: false
      // and bouncing them through /onboarding.
      if (signedInRole === 'trainer' || signedInRole === 'admin') {
        router.refresh()
        return { hasProfile: true, appRole: signedInRole }
      }

      // The onAuthStateChange listener will handle setting user and profile state.
      // We still check for the profile here to return the `hasProfile` status
      // to the login page component.
      try {
        const userProfile = await fetchProfileWithRetry(data.user.id)

        // Force a server-side refresh. This re-runs the middleware
        // with the new auth cookie, ensuring the server is aware of the new session.
        router.refresh()

        return { hasProfile: !!userProfile, appRole: signedInRole }
      } catch (error) {
        console.error('❌ [AUTH] Profile check error during sign in:', error)
        // Still return success for sign in, profile issues shouldn't block auth
        router.refresh()
        return { hasProfile: false, appRole: signedInRole }
      }
    }
    return { hasProfile: false, appRole: DEFAULT_APP_ROLE }
  }

  const value = { user, loading, profile, appRole, signIn, signOut, isSigningOut, supabase };

  // Show auth error state
  if (authError && !loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-purple-200">
        <div className="text-center space-y-6 max-w-md px-4">
          <div className="text-6xl mb-4">🔐</div>
          <h1 className="text-2xl font-semibold">Authentication Error</h1>
          <p className="text-purple-300/80">{authError}</p>
          <div className="flex gap-4 justify-center">
            <button 
              onClick={retryAuth}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              className="border border-purple-400 text-purple-300 hover:bg-purple-800/20 px-6 py-2 rounded-lg transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-purple-400 border-t-transparent"></div>
        </div>
      ) : (
        children
      )}
      <AnimatePresence>
        {isSigningOut && <InterstitialLoading title="See you soon!" subtitle="Redirecting to the landing page..." />}
      </AnimatePresence>
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 
